import { useEffect, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import type { CragRouteModelApi } from '../../../components/crag/CragRouteModel';
import {
  cameraAt,
  DEFAULT_MODEL_LIMITS,
  distanceToSlider,
  MODEL_LIMIT_BOUNDS,
  sliderToDistance,
  viewPolarDegrees,
  type ModelViewChange,
} from '../../../components/crag/model-limits';
import type { ModelCamera, ModelView, ModelViewLimits } from '../../../types/crag-route';

/** What holds the camera while the limits are being tried out. */
export type ModelTriedLimits = ModelCamera & { limits: ModelViewLimits };

interface SectorViewLimitsProps {
  savedView: ModelView | null;
  viewerApi: MutableRefObject<CragRouteModelApi | null>;
  /** Says whether it was saved: if not, the sliders stay as they are, to try again. */
  onSave: (view: { position: ModelCamera['position']; target: ModelCamera['target']; limits: ModelViewLimits | null }) => Promise<boolean>;
  onTry: (limits: ModelTriedLimits | null) => void;
}

/** Keeps a range around the view, which is what it is measured from: its own 0. */
function aroundZero([low, high]: number[]): [number, number] {
  return [Math.min(low, 0), Math.max(high, 0)];
}

function percent(share: number) {
  return `${Math.round(share * 100)}%`;
}

function signedDegrees(degrees: number) {
  return `${degrees > 0 ? '+' : ''}${Math.round(degrees)}°`;
}

/** A first draft, made to fit: a view from near the top cannot go 45° further up. */
function startingLimits(view: ModelCamera): ModelViewLimits {
  const polar = viewPolarDegrees(view);
  return {
    ...DEFAULT_MODEL_LIMITS,
    polar: [Math.max(DEFAULT_MODEL_LIMITS.polar[0], -polar), Math.min(DEFAULT_MODEL_LIMITS.polar[1], 180 - polar)],
  };
}

/**
 * How far a member may turn, zoom and slide this sector's model, measured from its
 * saved view. A thumb moved shows that limit on the model, so what a member would see
 * from there is on screen. The panel's own camera stays free ("Try as a member" apart),
 * since drawing a line needs to get closer than a member may.
 */
export default function SectorViewLimits({ savedView, viewerApi, onSave, onTry }: SectorViewLimitsProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ModelViewLimits | null>(null);
  /** With no saved view the camera at the moment of opening stands in, and is saved with them. */
  const [standIn, setStandIn] = useState<ModelCamera | null>(null);
  const [trying, setTrying] = useState(false);
  const [saving, setSaving] = useState(false);

  const base = savedView ?? standIn;
  const editing = draft !== null && base !== null;

  useEffect(() => {
    onTry(trying && editing ? { position: base.position, target: base.target, limits: draft } : null);
  }, [trying, editing, base, draft, onTry]);
  // Leaving the sector, or opening a route, leaves the camera free again.
  useEffect(() => () => onTry(null), [onTry]);

  const open = () => {
    const from = savedView ?? viewerApi.current?.currentView() ?? null;
    if (!from) return;
    setStandIn(savedView ? null : from);
    setDraft(savedView?.limits ?? startingLimits(from));
  };

  const close = () => {
    setDraft(null);
    setTrying(false);
  };

  const show = (change: ModelViewChange) => {
    if (base) viewerApi.current?.showView(cameraAt(base, change));
  };

  const save = async (limits: ModelViewLimits | null) => {
    if (!base) return;
    setSaving(true);
    try {
      if (await onSave({ position: base.position, target: base.target, limits })) close();
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    const limits = savedView?.limits;
    return (
      <section className="space-y-2 border-t pt-3" aria-label={t('cragAdmin.routes.limits.title')}>
        <h3 className="text-sm font-medium">{t('cragAdmin.routes.limits.title')}</h3>
        <p className="text-xs text-muted-foreground">
          {limits
            ? t('cragAdmin.routes.limits.summary', {
                horizontal: `${signedDegrees(limits.azimuth[0])} / ${signedDegrees(limits.azimuth[1])}`,
                vertical: `${signedDegrees(limits.polar[0])} / ${signedDegrees(limits.polar[1])}`,
                distance: `${percent(limits.distance[0])}–${percent(limits.distance[1])}`,
                pan: percent(limits.pan),
              })
            : t('cragAdmin.routes.limits.none')}
        </p>
        <Button type="button" size="sm" variant="outline" onClick={open}>
          {t(limits ? 'cragAdmin.routes.limits.edit' : 'cragAdmin.routes.limits.set')}
        </Button>
      </section>
    );
  }

  const polar = viewPolarDegrees(base);
  const moved = (next: [number, number], current: [number, number]) => (next[0] !== current[0] ? next[0] : next[1]);

  return (
    <section className="space-y-3 border-t pt-3" aria-label={t('cragAdmin.routes.limits.title')}>
      <h3 className="text-sm font-medium">{t('cragAdmin.routes.limits.title')}</h3>
      <p className="text-xs text-muted-foreground">
        {t(savedView ? 'cragAdmin.routes.limits.hint' : 'cragAdmin.routes.limits.hintNoView')}
      </p>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <Label id="limit-azimuth">{t('cragAdmin.routes.limits.horizontal')}</Label>
          <span className="text-xs tabular-nums text-muted-foreground">
            {signedDegrees(draft.azimuth[0])} / {signedDegrees(draft.azimuth[1])}
          </span>
        </div>
        <Slider
          aria-labelledby="limit-azimuth"
          min={-MODEL_LIMIT_BOUNDS.degrees}
          max={MODEL_LIMIT_BOUNDS.degrees}
          step={1}
          value={draft.azimuth}
          onValueChange={(values) => {
            const next = aroundZero(values);
            show({ azimuth: moved(next, draft.azimuth) });
            setDraft({ ...draft, azimuth: next });
          }}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <Label id="limit-polar">{t('cragAdmin.routes.limits.vertical')}</Label>
          <span className="text-xs tabular-nums text-muted-foreground">
            {signedDegrees(draft.polar[0])} / {signedDegrees(draft.polar[1])}
          </span>
        </div>
        <Slider
          aria-labelledby="limit-polar"
          min={Math.round(-polar)}
          max={Math.round(180 - polar)}
          step={1}
          value={draft.polar}
          onValueChange={(values) => {
            const next = aroundZero(values);
            show({ polar: moved(next, draft.polar) });
            setDraft({ ...draft, polar: next });
          }}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{t('cragAdmin.routes.limits.fromAbove')}</span>
          <span>{t('cragAdmin.routes.limits.fromBelow')}</span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <Label id="limit-distance">{t('cragAdmin.routes.limits.distance')}</Label>
          <span className="text-xs tabular-nums text-muted-foreground">
            {percent(draft.distance[0])}–{percent(draft.distance[1])}
          </span>
        </div>
        <Slider
          aria-labelledby="limit-distance"
          min={0}
          max={100}
          step={1}
          value={draft.distance.map(distanceToSlider)}
          onValueChange={([low, high]) => {
            const next: [number, number] = [
              low >= 50 ? 1 : sliderToDistance(low),
              high <= 50 ? 1 : sliderToDistance(high),
            ];
            show({ distance: moved(next, draft.distance) });
            setDraft({ ...draft, distance: next });
          }}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{t('cragAdmin.routes.limits.closer')}</span>
          <span>{t('cragAdmin.routes.limits.further')}</span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <Label id="limit-pan">{t('cragAdmin.routes.limits.pan')}</Label>
          <span className="text-xs tabular-nums text-muted-foreground">{percent(draft.pan)}</span>
        </div>
        <Slider
          aria-labelledby="limit-pan"
          min={0}
          max={MODEL_LIMIT_BOUNDS.maxPan * 100}
          step={5}
          value={[draft.pan * 100]}
          onValueChange={([value]) => {
            show({ pan: value / 100 });
            setDraft({ ...draft, pan: value / 100 });
          }}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={trying ? 'default' : 'outline'}
          aria-pressed={trying}
          onClick={() => {
            if (!trying) viewerApi.current?.showView(base, { fly: true });
            setTrying(!trying);
          }}
        >
          {t('cragAdmin.routes.limits.try')}
        </Button>
        <Button type="button" size="sm" disabled={saving} onClick={() => void save(draft)}>
          {t('cragAdmin.routes.limits.save')}
        </Button>
        {savedView?.limits && (
          <Button type="button" size="sm" variant="ghost" className="text-destructive" disabled={saving} onClick={() => void save(null)}>
            {t('cragAdmin.routes.limits.remove')}
          </Button>
        )}
        <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={close}>
          {t('common.cancel')}
        </Button>
      </div>
    </section>
  );
}
